<template>
  <ul class="nav font-condensed flex-grow-1 d-none d-md-flex ps-3">
    <li v-for="(item, i) in menuitems"
        :key="'main-menu-desktop-item_' + i"
        class="nav-item position-relative d-flex ps-2"
        :class="{ 'active': item === activeMenuItem }"
        :data-test-id="'main-menu-desktop-item_' + i"
    >
      <a class="nav-link p-0 d-flex align-items-center main-nav-link"
         :class="{ 'disabled': item.disabled }"
         :href="item.disabled ? undefined : item.path"
         :aria-disabled="item.disabled || undefined"
         :tabindex="item.disabled ? '-1' : undefined"
      >
        <b-icon v-if="item.icon && item.icon !== 'null'" :icon="item.icon" class="d-inline-flex align-items-center" />
        <span class="d-none d-lg-inline-flex align-items-center ms-1 text-uppercase">{{ item.title }}</span>
        <template v-if="item.title == 'My Dealership'">
          <b-icon icon="caret-up" class="ms-1 caret-up-icon" />
          <b-icon icon="caret-down" class="ms-1 caret-down-icon" />
        </template>
      </a>

      <!-- subnavigation is a sibling of <a>, not nested inside it -->
      <nav v-if="item.items"
           class="subnavigation d-none bg-white d-md-flex box-shadow rounded p-0 py-2 font-condensed"
           :data-test-id="'main-menu-desktop-subnav_' + i"
      >
        <ul class="list-unstyled mb-0 w-100">
          <li v-for="(s, j) in item.items"
              :key="'main-menu-desktop-subnav_' + i + '_item_' + j"
              :data-test-id="'main-menu-desktop-subnav_' + i + '_item_' + j"
              :class="{ 'active': isCurrentPath(s.path, true), 'disabled': s.disabled }"
              @click.stop
          >
            <a class="dropdown-item p-3 w-100 text-start"
               :class="{ 'active': isCurrentPath(s.path, true) }"
               :href="s.disabled ? undefined : s.path"
               :aria-disabled="s.disabled || undefined"
            >
              <span :class="{ 'invisible': !s.icon || s.icon === 'null' }">
                <b-icon v-if="s.icon && s.icon !== 'null'" :icon="s.icon" fixed-width />
                <b-icon v-else-if="hasIcons(item)" :icon="'cog'" class="invisible" fixed-width />
              </span>
              <span>{{ s.title }}</span>
            </a>
          </li>
        </ul>
      </nav>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { toRef } from 'vue'
import { MenuItem } from '../defaultMenuItems'
import { useActiveMenu } from '../composables/useActiveMenu'

interface Props {
  menuitems: MenuItem[]
}

const props = defineProps<Props>()

const { activeMenuItem, isCurrentPath, hasIcons } = useActiveMenu(toRef(props, 'menuitems'))
</script>

<style lang="scss" scoped>
@import '../scss/colors';
@import '../scss/grid';
@import '../scss/z-index';
@import '../scss/transitions';

.nav {
  .nav-item {
    
    .bicon {
      font-size: 1.1rem;
    }

    .caret-up-icon {
      display: none !important;
    }


    &.active::after {
      content: '';
      position: absolute;
      left: 10px;
      right: 0;
      bottom: 0;
      height: .25rem;
      background-color: $primary;
    }

    :deep(.main-nav-link:not(.disabled)) {
      transition: $transition-base;
      opacity: .7;

      > span {
        color: $white;
      }

      &:active,
      &:focus {
        opacity: 1;
      }

      &:hover {
        box-shadow: 0 .5rem 1rem rgba($black, .15);
        .caret-up-icon {
          display: inline-block !important;
        }

        .caret-down-icon {
          display: none !important;
        }
      }
    }

    :deep(.main-nav-link.disabled) {
      color: $gray-800;
    }

    &:active,
    &.active,
    &:focus,
    &.hover,
    &:hover {
      :deep(.main-nav-link) {
        opacity: 1;
      }
    }

    .subnavigation {
      color: inherit;
      position: absolute;
      top: -1000px;
      transition: $transition-fade;
      opacity: 0;
      padding: 0 !important;
      overflow: hidden;
      min-width: 15rem;
      max-width: 40rem;
      margin-top: 0.80rem;
      margin-left: 0rem;
      cursor: default;

      :deep(li:not(:first-child)) {
        border-top: 1px solid #dcdcdc;
      }

      :deep(a.dropdown-item) {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        transition: $transition-base;
        line-height: 1;
        border: 0;
        border-radius: 0;
        text-align: left;
        width: 100%;
        box-sizing: border-box;


        .bicon {
          flex-shrink: 0;
        }

        &:active,
        &.active {
          color: $black;
          background-color: transparent;
          text-decoration: none;
          box-shadow: inset 0 -0.25rem 0 $primary;
        }

        &.hover,
        &:hover,
        &:focus {
          background-color: $gray-400;
          color: $black;
          margin: 0;
          width: 100%;
        }
      }
    }

    &:hover {

      .subnavigation {
        opacity: 1;
        top: 3.2rem;
        z-index: $zindex-modal;
        overflow: visible;
      }
    }
  }
}
</style>
